/**
 * Date formatting utilities for the application
 * 
 * Backend stores all dates in UTC. These utilities convert UTC dates
 * to New York timezone (America/New_York) for display in the frontend.
 */

const TIMEZONE = 'America/New_York';

/**
 * Format a date string or Date object to a localized date string in New York timezone
 * 
 * Assumes the input date is in UTC (as stored by the backend).
 * Converts and displays it in New York timezone.
 * 
 * @param date - Date string (ISO format from backend) or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string in New York timezone
 */
export const formatDate = (
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return '';
  
  // If it's a string (from backend), it's already in UTC/ISO format
  // JavaScript Date will parse it correctly
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  // Format in New York timezone - Intl.DateTimeFormat automatically
  // handles the conversion from UTC to the specified timezone
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    ...options,
  }).format(dateObj);
};

/**
 * Format a date to a short date string (MM/DD/YYYY) in New York timezone
 */
export const formatDateShort = (date: string | Date | null | undefined): string => {
  return formatDate(date, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * Format a date to a long date string (e.g., "January 13, 2026") in New York timezone
 */
export const formatDateLong = (date: string | Date | null | undefined): string => {
  return formatDate(date, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date to a medium date string (e.g., "Jan 13, 2026") in New York timezone
 */
export const formatDateMedium = (date: string | Date | null | undefined): string => {
  return formatDate(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date and time string in New York timezone
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  return formatDate(date, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
};

/**
 * Format a time string in New York timezone
 */
export const formatTime = (
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  return formatDate(date, {
    hour: '2-digit',
    minute: '2-digit',
    second: options?.second ? '2-digit' : undefined,
    ...options,
  });
};

/**
 * Format a date range in New York timezone
 */
export const formatDateRange = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  const start = formatDate(startDate, options);
  const end = formatDate(endDate, options);
  
  if (!start && !end) return '';
  if (!start) return end;
  if (!end) return start;
  
  return `${start} - ${end}`;
};

/**
 * Get current date/time in New York timezone
 */
export const getNowInNY = (): Date => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: TIMEZONE }));
};

/**
 * Format a Unix timestamp (seconds since epoch) to New York timezone
 * Backend sometimes returns timestamps as Unix seconds
 */
export const formatUnixTimestamp = (
  timestamp: number | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!timestamp) return '';
  // Convert seconds to milliseconds
  const date = new Date(timestamp * 1000);
  return formatDate(date, options);
};

/**
 * Format a Unix timestamp to date and time string
 */
export const formatUnixDateTime = (timestamp: number | null | undefined): string => {
  return formatUnixTimestamp(timestamp, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format a Unix timestamp to time string only
 */
export const formatUnixTime = (timestamp: number | null | undefined): string => {
  return formatUnixTimestamp(timestamp, {
    hour: '2-digit',
    minute: '2-digit',
  });
};
